import { useEffect, useState } from "react";

/**
 * Return `value` only once it has stopped changing for `delayMs`.
 *
 * WHY THIS IS A HOOK AND NOT A `setTimeout` IN THE COMPONENT
 * ----------------------------------------------------------
 * Debouncing a search box is easy to write and easy to write WRONG. The two
 * mistakes this exists to prevent, both of which produce bugs that only appear
 * under a fast typist or a slow network:
 *
 * 1. Creating the timer inside an event handler instead of an effect. The timer
 *    then outlives the component, so a picker closed mid-search still fires a
 *    state update - React logs a leak warning and, worse, a stale term can win.
 *
 * 2. Forgetting the cleanup. Without it every keystroke leaves its own pending
 *    timer, so typing "norauto" fires SEVEN requests spaced one keystroke apart
 *    rather than one. The clearTimeout below is the entire point: each render
 *    with a new `value` cancels the previous pending commit.
 *
 * The result is used as a react-query key, so the query is not merely delayed -
 * it is never created for intermediate terms, and no request is issued for them
 * at all.
 *
 * @param value   the live value (e.g. the raw input text)
 * @param delayMs quiet period before the value is committed
 */
export function useDebouncedValue<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}
